import { useReferenceLabel } from "components/Form/components/ReferenceLabel/useReferenceLabel";
import RecordLabel from "components/RecordLabel";
import { FeAllType, FeByType, FeRecordType } from "config/config";
import customObjectConfig from "config/resources/custom_object";
import { getRelativeUrl } from "utils/service";

export interface SchemaFormReadonlyReferenceProps {
    elementSchema: any;
    value: any;
    dataPath: (string | number)[];
}

/**
 * Read-only display for a RECORD_REFERENCE (form-autocomplete) custom-object
 * attribute in list-view cells / dashboard record-table widgets.
 *
 * The interactive Autocomplete renders a bordered control box that reads as a
 * clickable button, and shows "Select an option" when empty — a false affordance
 * in read-only list views (there is no inline edit for references yet, see
 * operator-task-fe-inline-edit-list-views). Instead, render the referenced
 * record as plain linked text (RecordLabel), and show "No <type> selected" when
 * empty.
 *
 * The id -> name label is resolved through the shared, batched reference-label
 * resolver (useReferenceLabel): every read-only reference cell on a screen that
 * points at the same resource is resolved with ONE `{id in [...]}` query and a
 * single (resource, id)-keyed cache entry, rather than one single-id fetch and
 * one redux slice per cell. Name-stored references need no fetch — their value
 * is already the display name.
 */
export const SchemaFormReadonlyReference = ({
    elementSchema,
    value,
}: SchemaFormReadonlyReferenceProps) => {
    const recordType: string | undefined = elementSchema.record_type?.toLowerCase();
    const hasNameAsOptionValue =
        elementSchema.store === "record_name_existing" ||
        elementSchema.store === "record_name_new";

    const isEmpty = value == null || value === "";

    // Resolve the display label for id-stored references. Name-stored references
    // already carry the display name as their value, so we skip resolution
    // (resource === undefined) and render the value directly.
    const entry = useReferenceLabel(
        !hasNameAsOptionValue ? recordType : undefined,
        isEmpty ? undefined : String(value)
    );

    if (isEmpty) {
        const typeLabel =
            recordType && recordType !== customObjectConfig.resourceName
                ? recordType.replace(/_/g, " ")
                : null;
        const placeholder = typeLabel ? `No ${typeLabel} selected` : "No record selected";
        return <div className="schemaFormReadonlyText text-dark-grey">{placeholder}</div>;
    }

    const label = entry?.label ?? String(value);
    const by: FeByType = hasNameAsOptionValue ? "name" : "id";
    const link = recordType
        ? getRelativeUrl(recordType as FeAllType, String(value), by)
        : null;

    return (
        <RecordLabel
            name={label}
            link={link}
            iconByType={recordType as FeRecordType}
        />
    );
};
